import { useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { atom, useRecoilState } from 'recoil';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import {
    checkIsPaidSiteProductKey,
    getSiteProductDefinition,
    isFreeSiteType,
    isLegacySiteType,
    isSiteTypeDowngrade,
    type LegacySitePricingDefinition,
    SITE_PRICING_PRODUCTS,
    SitePlanTarget,
    type SitePricingDefinition,
} from '@gitbook/app-pricing';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    type ButtonProps,
    Description,
    Dialog,
    DialogBody,
    DialogBrandedLayout,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    DialogRoot,
    FieldError,
    Form,
    Heading,
    RadioGroup,
    RadioGroupField,
    toast,
    useDialogAction,
} from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';
import { assert, type MandateProps } from '@gitbook/util-ts';

import {
    FeatureIncludedBadge,
    SiteFeatureLossAlert,
    SiteTypeCardFeaturesList,
    SiteTypeCardRadio,
} from '../../../../Components';
import { useSetAnalyticsScreen } from '../../../../navigation';
import { apiQuery, assertOrganizationWithBilling, useTrial } from '../../../../state';
import { SitePricingModalAside } from '../SitePricingModalAside';
import TrialBadge from './TrialBadge';

/**
 * A dialog to change the site plan.
 */

// Filter out the Sponsored plan since it's tied to Community orgs and not self-serve
const SELF_SERVE_SITE_PLANS = SITE_PRICING_PRODUCTS.filter(
    // The Sponsored plan is tied to Community orgs and is not self-serve.
    ({ key }) => key !== api.SiteType.Sponsored
);

export enum ChangeSitePlanDialogLayout {
    Regular = 'regular',
    Branded = 'branded',
}

type ChangeSitePlanDialogState = {
    /** The site for which the plan change dialog is being shown */
    site: api.Site;

    /** Optional. Override the site type - for in-memory changes */
    siteTypeOverride?: api.SiteType;

    /** The organization the site belongs to */
    organization: api.Organization;

    /** The visual layout variant for the dialog */
    layout: ChangeSitePlanDialogLayout;

    /** The reason why the plan change dialog is being shown */
    reason: api.SiteUpgradeReason;

    /** Callback function to handle the site plan change action */
    onChangeSitePlan: (params: {
        site: api.Site;
        selectedSitePlan: api.SiteType;
        reason: api.SiteUpgradeReason;
        closeDialog: () => void;
    }) => Promise<void>;
};

const changeSitePlanDialogState = atom<ChangeSitePlanDialogState | null>({
    key: 'changeSitePlanDialog',
    default: null,
});

/**
 * Hook to manage the change site plan dialog state.
 * Provides functions to open and close the dialog, along with access to the current dialog state.
 */
export function useChangeSitePlanDialog(): Partial<ChangeSitePlanDialogState> & {
    openChangeSitePlanDialog: (params: ChangeSitePlanDialogState) => void;
    closeChangeSitePlanDialog: () => void;
} {
    const [state, setState] = useRecoilState(changeSitePlanDialogState);

    const openChangeSitePlanDialog = useEventCallback((params: ChangeSitePlanDialogState) => {
        analytics.logEvent(
            analytics.createSiteEvent(
                { id: params.site.id, organization: params.organization.id },
                {
                    type: 'site_open_change_plan_dialog',
                    reason: params.reason,
                    currentPlan: params.site.type,
                    selectedPlan: params.siteTypeOverride ?? params.site.type,
                }
            )
        );
        setState({
            site: params.site,
            siteTypeOverride: params.siteTypeOverride,
            organization: params.organization,
            layout: params.layout,
            reason: params.reason,
            onChangeSitePlan: params.onChangeSitePlan,
        });
    });

    const closeChangeSitePlanDialog = useEventCallback(() => {
        setState(null);
    });

    const onChangeSitePlan = state?.onChangeSitePlan ?? (() => Promise.resolve());

    return {
        site: state?.site,
        siteTypeOverride: state?.siteTypeOverride,
        organization: state?.organization,
        layout: state?.layout,
        reason: state?.reason,
        onChangeSitePlan,
        openChangeSitePlanDialog,
        closeChangeSitePlanDialog,
    };
}

/**
 * Dialog loader component.
 * Does mostly validation and renders the actual dialog if all the required state is available.
 */
export function ChangeSitePlanDialog() {
    const {
        site,
        siteTypeOverride,
        organization,
        layout,
        reason,
        onChangeSitePlan,
        closeChangeSitePlanDialog,
    } = useChangeSitePlanDialog();

    if (!site || !organization || !layout || !reason || !onChangeSitePlan) {
        return null;
    }

    assertOrganizationWithBilling(organization);

    const isNewPricing = !isLegacySiteType(site.type);
    assert(isNewPricing, 'ChangeSitePlanDialog should not be rendered for legacy site types');

    return (
        <DialogRoot
            isOpen
            onOpenChange={(isOpen) => {
                if (!isOpen) {
                    closeChangeSitePlanDialog();
                }
            }}
        >
            <ChangeSitePlanDialogContent
                site={site}
                siteTypeOverride={siteTypeOverride}
                organization={organization}
                layout={layout}
                reason={reason}
                onChangeSitePlan={onChangeSitePlan}
            />
        </DialogRoot>
    );
}

/**
 * The dialog body component.
 * Manages the form state for plan selection and the callback, including error handling.
 */
function ChangeSitePlanDialogContent(
    props: ChangeSitePlanDialogState & {
        organization: MandateProps<api.Organization, 'billing'>;
    }
) {
    const { site, siteTypeOverride, organization, layout, reason, onChangeSitePlan } = props;

    const [isPending, startDialogAction] = useDialogAction();

    useSetAnalyticsScreen(
        analytics.createSiteEvent(
            { id: site.id, organization: organization.id },
            {
                screen: 'site_plan_modal_plans',
                isNewPricing: true,
                reason,
            }
        ),
        [site.id, organization.id]
    );

    const currentSiteType = siteTypeOverride ?? site.type;
    const form = useForm({ defaultValues: { siteType: currentSiteType } });
    const selectedSiteType = form.watch('siteType') ?? currentSiteType;
    const selectedPlanDefinition = getSiteProductDefinition(selectedSiteType);
    const isDowngrade = isSiteTypeDowngrade(currentSiteType, selectedSiteType);

    const handleSiteTypeChange = async (isPlanSwitch: boolean) => {
        await startDialogAction(async ({ close }) => {
            await toast.catch(
                async () => {
                    await onChangeSitePlan({
                        site,
                        selectedSitePlan: selectedSiteType,
                        reason,
                        closeDialog: close,
                    });
                },
                `Error while ${isPlanSwitch ? 'switching' : isDowngrade ? 'downgrading' : 'upgrading'} site plan`
            );
        });
    };

    const isBranded = layout === ChangeSitePlanDialogLayout.Branded;
    const Layout = isBranded ? DialogBrandedLayout : React.Fragment;

    return (
        <Dialog size={{ width: isBranded ? 850 : 600 }}>
            <Layout
                aside={{
                    content: (
                        <React.Suspense fallback={null}>
                            <SitePricingModalAside requiredSiteType={selectedSiteType} />
                        </React.Suspense>
                    ),
                }}
            >
                <DialogHeader>
                    <Heading>Change your site plan</Heading>
                    <Description>
                        Your site plan is independent of your organization plan. You can change the
                        plan at any time, which may impact active features.
                    </Description>
                </DialogHeader>

                <DialogBody>
                    <Form form={form}>
                        <RadioGroupField name="siteType" required>
                            <RadioGroup aria-label="Site plans">
                                {SELF_SERVE_SITE_PLANS.map(({ key, subtitle, title }) => {
                                    const isPaid = checkIsPaidSiteProductKey(key);
                                    const isActive = key === selectedSiteType;
                                    const showFeatureLoss = isActive && isDowngrade;

                                    return (
                                        <SiteTypeCardRadio
                                            key={key}
                                            billing={organization.billing}
                                            siteType={key}
                                            title={
                                                <div className="flex items-center justify-start gap-2">
                                                    {title}
                                                    {isPaid ? (
                                                        <React.Suspense fallback={null}>
                                                            <TrialBadge
                                                                organization={organization}
                                                            />
                                                            <FeatureIncludedBadge />
                                                        </React.Suspense>
                                                    ) : null}
                                                </div>
                                            }
                                            description={subtitle}
                                            featuresList={
                                                isPaid || showFeatureLoss ? (
                                                    <div className="flex w-full flex-col gap-4">
                                                        {isPaid ? (
                                                            <SiteTypeCardFeaturesList
                                                                siteType={key}
                                                                target={SitePlanTarget.CUSTOMERS}
                                                                columns={2}
                                                            />
                                                        ) : null}
                                                        {showFeatureLoss ? (
                                                            <SiteFeatureLossAlert
                                                                site={site}
                                                                toPlan={selectedSiteType}
                                                            />
                                                        ) : null}
                                                    </div>
                                                ) : null
                                            }
                                            cardHighlight={
                                                key === currentSiteType ? 'Current plan' : undefined
                                            }
                                        />
                                    );
                                })}
                            </RadioGroup>
                            <FieldError />
                        </RadioGroupField>
                    </Form>
                </DialogBody>

                <DialogFooter>
                    <DialogCloseButton label="Close" />
                    {selectedSiteType !== currentSiteType ? (
                        <React.Suspense fallback={<Button label="Loading..." disabled />}>
                            <ChangeSitePlanButton
                                organization={organization}
                                site={site}
                                currentSiteType={currentSiteType}
                                selectedSiteType={selectedSiteType}
                                selectedPlanDefinition={selectedPlanDefinition}
                                isDowngrade={isDowngrade}
                                handleSiteTypeChange={handleSiteTypeChange}
                                loadable={isPending ? [true] : undefined}
                            />
                        </React.Suspense>
                    ) : null}
                </DialogFooter>
            </Layout>
        </Dialog>
    );
}

/**
 * Button component for changing site plans.
 * The button fetches a plan update preview and adapts its style and copy based on the response.
 */
function ChangeSitePlanButton(
    props: Pick<ButtonProps, 'loadable'> & {
        organization: MandateProps<api.Organization, 'billing'>;
        site: api.Site;
        currentSiteType: api.SiteType;
        selectedSiteType: api.SiteType;
        selectedPlanDefinition: SitePricingDefinition | LegacySitePricingDefinition;
        isDowngrade: boolean;
        handleSiteTypeChange: (isPlanSwitch: boolean) => Promise<void>;
    }
) {
    const {
        organization,
        site,
        currentSiteType,
        selectedSiteType,
        selectedPlanDefinition,
        isDowngrade,
        handleSiteTypeChange,
        ...buttonProps
    } = props;

    const { data: preview } = useSuspenseQuery(
        apiQuery('previewSitePlanUpdate', {
            organizationId: organization.id,
            siteId: site.id,
            plan: selectedSiteType,
        })
    );

    const trial = useTrial({ organization });
    const canStartTrial = trial.status === api.BillingTrialStatus.None;
    const isPlanSwitch = preview?.type === 'go-ahead';

    const getButtonState = (): ButtonProps => {
        if (isPlanSwitch) {
            return {
                kind: 'primary',
                label:
                    canStartTrial && !isFreeSiteType(selectedPlanDefinition.key)
                        ? `Try ${selectedPlanDefinition.title}`
                        : 'Switch plan',
            };
        }

        if (isDowngrade) {
            return {
                kind: 'danger',
                label: `Downgrade to ${selectedPlanDefinition.title}`,
            };
        }

        return {
            kind: 'upgrade',
            label: `Upgrade to ${selectedPlanDefinition.title}`,
            leadingIcon: Icon.Upgrade,
        };
    };

    return (
        <Button
            {...getButtonState()}
            {...buttonProps}
            onPress={() => handleSiteTypeChange(isPlanSwitch)}
        />
    );
}
